import type { Data, Layout } from 'plotly.js'
import type {
  ExcursionMetricsData,
  ExcursionMetricsProduct,
} from '@/api/use-excursion-metrics-query'

const PARETO_BAR_COLOR = '#f59e0b'
const PARETO_BAR_LINE_COLOR = '#d97706'
const INACTIVE_BAR_COLOR = '#e5e7eb'
const INACTIVE_BAR_LINE_COLOR = '#d4d4d4'
const MAX_PRODUCTS = 10

export type ProductMetricsChartOptions = {
  /** When non-empty, only these product labels use the active bar color. */
  activeProducts?: string[]
}

export function productMetricsChartLabel(p: ExcursionMetricsProduct): string {
  return p.product_name?.trim() || p.product_id || 'Unknown'
}

/** Product labels shown on the dashboard product metrics chart (top N by wafers impacted). */
export function productMetricsChartLabels(metrics: ExcursionMetricsData): string[] {
  return sortAndLimitProducts(metrics.products).map(productMetricsChartLabel)
}

function sortAndLimitProducts(products: ExcursionMetricsProduct[]): ExcursionMetricsProduct[] {
  return [...products]
    .sort((a, b) => {
      const d = b.wafers_impacted - a.wafers_impacted
      if (d !== 0) return d
      return productMetricsChartLabel(a).localeCompare(productMetricsChartLabel(b))
    })
    .slice(0, MAX_PRODUCTS)
}

function barColorsForLabels(
  labels: string[],
  activeProducts: string[] | undefined,
): { colors: string[]; lineColors: string[] } {
  const filtering = (activeProducts?.length ?? 0) > 0
  return {
    colors: labels.map((label) =>
      !filtering || activeProducts!.includes(label) ? PARETO_BAR_COLOR : INACTIVE_BAR_COLOR,
    ),
    lineColors: labels.map((label) =>
      !filtering || activeProducts!.includes(label)
        ? PARETO_BAR_LINE_COLOR
        : INACTIVE_BAR_LINE_COLOR,
    ),
  }
}

export function buildProductMetricsChartFigure(
  metrics: ExcursionMetricsData,
  layoutBase: Record<string, unknown>,
  chartTitle: (text: string) => Layout['title'],
  options?: ProductMetricsChartOptions,
): { data: Data[]; layout: Partial<Layout> } {
  const sorted = sortAndLimitProducts(metrics.products)
  const productLabels = sorted.map(productMetricsChartLabel)

  const axisCommon = {
    tickangle: -40,
    automargin: true,
    gridcolor: 'transparent',
    linecolor: '#e5e7eb',
    linewidth: 1,
    tickfont: { size: 11 },
    standoff: 10,
    fixedrange: true,
  }

  const impactLayout = {
    dragmode: false as const,
    xaxis: axisCommon,
    yaxis: {
      title: { text: 'Wafers impacted', standoff: 8, font: { size: 11, color: '#737373' } },
      gridcolor: '#f3f4f6',
      gridwidth: 1,
      zeroline: false,
      linecolor: 'transparent',
      fixedrange: true,
    },
  }

  if (sorted.length === 0) {
    return {
      data: [
        {
          type: 'bar',
          name: 'Wafers impacted',
          x: ['No product data'],
          y: [0],
          marker: {
            color: '#e5e7eb',
            line: { color: '#d4d4d4', width: 0 },
          },
          hovertemplate: 'No per-product metrics in this scope<extra></extra>',
          visible: true,
        },
      ],
      layout: {
        ...layoutBase,
        title: chartTitle('Product metrics'),
        barmode: 'group',
        bargap: 0.25,
        bargroupgap: 0.08,
        showlegend: false,
        margin: { ...(layoutBase.margin as object), b: 72 },
        ...impactLayout,
      },
    }
  }

  const { colors, lineColors } = barColorsForLabels(productLabels, options?.activeProducts)

  const data: Data[] = [
    {
      type: 'bar' as const,
      name: 'Wafers impacted',
      x: productLabels,
      y: sorted.map((p) => p.wafers_impacted),
      marker: {
        color: colors,
        line: { color: lineColors, width: 0 },
        opacity: 1,
      },
      hovertemplate: '<b>%{x}</b><br>Wafers impacted: <b>%{y:,}</b><extra></extra>',
    },
  ]

  const baseMargin = layoutBase.margin as
    | { t?: number; r?: number; b?: number; l?: number }
    | undefined

  return {
    data,
    layout: {
      ...layoutBase,
      title: chartTitle('Product metrics'),
      barmode: 'group',
      bargap: 0.2,
      bargroupgap: 0.06,
      showlegend: false,
      margin: {
        ...baseMargin,
        t: baseMargin?.t ?? 50,
        b: 120,
        r: baseMargin?.r ?? 20,
      },
      ...impactLayout,
    },
  }
}
